import React, { useEffect } from "react"
import * as Sentry from "@sentry/react"
import { getStaticFeatures } from "../hooks/use-feature"
import { sendBeacon } from "../utils/send-beacon"
import {
  SENTRY_CONFIG,
  shouldEnableSentry,
  shouldFilterError,
  getBaseSentryConfig
} from "../lib/sentry-config"

/**
 * Log a frontend error to Kibana via /ui/log, regardless of whether it is
 * also sent to Sentry. Mirrors what ErrorReporter.log does in the backend
 * before_send: every exception gets a story so we have complete coverage in
 * Kibana even after an error is suppressed from Sentry.
 *
 * Search in Kibana with: message:"frontend-error-reporting"
 * Filter to non-Sentry errors: filter_reason_string:*
 * Filter to Sentry-bound errors: is_sentry_sent:true
 */
function logErrorToKibana(errorName, errorValue, filterReason, event) {
  const path = window.location.pathname.replace(/\d+/g, ":id")

  const frames = event?.exception?.values?.[0]?.stacktrace?.frames
  const crashFrame = frames?.[frames.length - 1]
  const backtrace_top = crashFrame?.filename
    ? `${crashFrame.filename}:${crashFrame.lineno ?? "?"}`
    : null

  sendBeacon(
    "/ui/log",
    JSON.stringify({
      event: "frontend-error-reporting",
      error_class_string: (errorName || "UnknownError").substring(0, 200),
      error_message_string: (errorValue || "").substring(0, 500),
      filter_reason_string: filterReason || null,
      is_sentry_sent: !filterReason,
      release_string: process.env.SHORT_CIRCLE_SHA1 || null,
      path_string: path,
      transaction_string: event?.transaction || path,
      backtrace_top_string: backtrace_top
    })
  )
}

const shouldReportError =
  shouldEnableSentry() &&
  SENTRY_CONFIG.allowedHosts.includes(window.location.host)

if (shouldReportError) {
  Sentry.init({
    ...getBaseSentryConfig(),
    enabled: shouldReportError,
    integrations: [
      Sentry.browserTracingIntegration({
        // Only create spans for API requests, not static assets from CDN
        shouldCreateSpanForRequest: url => {
          // Exclude CDN domains (JS chunks, static assets) - from shared config
          if (SENTRY_CONFIG.cdnDomains.some(domain => url.includes(domain))) {
            return false
          }

          // Check for blocklisted paths (handles both relative and absolute URLs)
          if (
            SENTRY_CONFIG.blocklistedPaths?.some(path => {
              // For relative URLs: direct match
              if (url.startsWith(path)) return true
              // For absolute URLs: check if pathname starts with the path
              try {
                return new URL(url).pathname.startsWith(path)
              } catch {
                return false
              }
            })
          ) {
            return false
          }

          // Relative paths starting with "/" are requests to our own app
          if (url.startsWith("/")) {
            return true
          }

          // For full URLs, check if hostname ends with one of our app domains
          // This prevents false matches in query params like "?redirect=https://app.kit.com"
          try {
            const urlObj = new URL(url)
            return SENTRY_CONFIG.appDomains.some(
              domain =>
                urlObj.hostname === domain ||
                urlObj.hostname.endsWith("." + domain)
            )
          } catch {
            // If URL parsing fails, check if URL starts with domain or https://domain
            return SENTRY_CONFIG.appDomains.some(
              domain =>
                url.startsWith(domain) ||
                url.startsWith("https://" + domain) ||
                url.startsWith("http://" + domain)
            )
          }
        }
      }),
      Sentry.replayIntegration({
        maskAllText: SENTRY_CONFIG.replay.maskAllText,
        maskAllInputs: SENTRY_CONFIG.replay.maskAllInputs,
        blockAllMedia: SENTRY_CONFIG.replay.blockAllMedia
      })
    ],

    beforeSend(event, hint) {
      const error = hint.originalException
      const errorName = error?.name || event.exception?.values?.[0]?.type || ""
      const errorValue =
        error?.message || event.exception?.values?.[0]?.value || ""

      // Errors flagged with `bypassFilters` (e.g. set by React error boundaries
      // on page-crashing errors) skip the noise filters but still get logged.
      const bypassFilters = Boolean(event?.contexts?.custom?.bypassFilters)

      const filterReason = bypassFilters
        ? null
        : shouldFilterError(error, errorName, errorValue, event) || null

      // Log every error to Kibana regardless of whether it goes to Sentry.
      // Mirrors ErrorReporter.log in the Ruby before_send: complete coverage
      // so filtered errors are never silently dropped.
      logErrorToKibana(errorName, errorValue, filterReason, event)

      if (filterReason) return null

      // Slate normalization-loop errors throw from native DOM event handlers,
      // so they bypass EditorErrorBoundary and arrive here unenriched. The
      // editor registers this hook on window when it mounts (see
      // normalization-diagnostics-plugin) — the layout bundle must not import
      // editor code directly.
      if (errorValue.includes("Could not completely normalize")) {
        try {
          const normalization = window.__editorNormalizationContext?.()
          if (normalization) {
            event.contexts = event.contexts || {}
            event.contexts.normalization = normalization
          }
        } catch {
          // Diagnostics must never block the error report itself.
        }
      }

      // Track errors to analytics
      if (window.analytics) {
        const user = window.app?.user

        window.analytics.track("error", {
          path: window.location.pathname.replace(/\d+/g, ":id"),
          context: event.contexts,
          user_id: user?.id,
          account_id: user?.account_id,
          class_name: errorName
        })
      }

      // Attach static features
      const features = getStaticFeatures()
      if (features.length > 0) {
        event.contexts = event.contexts || {}
        event.contexts.features = features.reduce(
          (acc, feat) => ({ ...acc, [feat]: true }),
          {}
        )
      }

      return event
    }
  })
}

// Provide fallback ErrorBoundary when Sentry is not initialized
const ErrorBoundary = !shouldReportError
  ? ({ children }) => children
  : Sentry.ErrorBoundary

export function SentryErrorBoundary({ children }) {
  useEffect(() => {
    // Only set user data if Sentry is initialized
    if (!shouldReportError || !window.app) {
      return
    }
    const user = window.app.user

    // Set user identification in Sentry
    Sentry.setUser({
      id: user?.id,
      email: user?.email
    })

    // Add account metadata
    Sentry.setContext("account", {
      id: user?.account_id,
      created_at: user?.account_created_at
    })

    // Add admin context if impersonating
    if (user?.admin) {
      Sentry.setContext("admin", {
        id: window.app.admin_user?.id,
        email: window.app.admin_user?.email
      })
    }
  }, [])

  return <ErrorBoundary>{children}</ErrorBoundary>
}

window.SentryClient = Sentry
export { Sentry }
